import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import cx from "classnames";
import React, { useEffect, useState } from "react";
import { useController, useFormContext, useFormState } from "react-hook-form";
import {
  useGetFieldLabel,
  useGetFieldPlaceholder,
} from "../../contexts/LanguageContext";
import {
  TApplicationField,
  TApplicationFieldOption,
} from "../../hooks/application-fields.hooks";
import { useUploadFile } from "../../hooks/applications.hooks";
import { useMediaQuery } from "../../hooks/common";
import { TDocumentConfig } from "../../hooks/forms.hooks";
import { VALIDATORS } from "../../utils/validators";
import { DocumentPreviewDialog } from "../CommonComponents/DialogConfirmation/DocPreviewDialog";
import {
  DeleteButton,
  ViewButton,
} from "../CommonComponents/EditComponent/EditComponent";
import Input, { InputProps } from "../CommonComponents/Input/Input";
import Select from "../CommonComponents/Select/Select";
import { ErrorToast } from "../toastMessage";
type PropTypes = {
  disabled?: boolean;
  validator?: string;
  field: TApplicationField & {
    options?: TApplicationFieldOption[];
  };
};

function RenderInput({ validator, field, disabled }: PropTypes) {
  const { register } = useFormContext();
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const info = useFormState({ name: field.uid });

  let props: InputProps = {};
  let registerOpts: Parameters<typeof register>[1] = undefined;

  switch (field.type) {
    case "integer":
    case "area":
    case "float": {
      const blockInvalidChar: React.KeyboardEventHandler<HTMLInputElement> = (
        e
      ) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

      props = { type: "number", onKeyDown: blockInvalidChar };
      registerOpts = {
        valueAsNumber: true,
        min: { value: 0, message: "Cannot be less than 0" },
      };
      break;
    }
    case "date": {
      props = { type: "date" };
      registerOpts = { valueAsDate: true };
      break;
    }
  }
  return (
    <Input
      label={convert(field)}
      required={!!field?.mandatory}
      error={!!info?.errors?.[field.uid]}
      placeholder={convertPlaceholder(field)}
      disabled={disabled ?? false}
      helperText={info?.errors?.[field.uid]?.message?.toString?.() || ""}
      {...props}
      {...register(field.uid, {
        required: field.mandatory ? "* Required" : false,
        ...(!!validator &&
          !!VALIDATORS[validator] && { validate: VALIDATORS[validator] }),
        ...registerOpts,
      })}
    />
  );
}

function RenderArea({ field, disabled }: PropTypes) {
  const { register, getValues } = useFormContext();
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const info = useFormState({ name: field.uid });

  let options = ["sq. mt.", "sq. ft."];

  const unitName = `unit_${field.uid}`;

  const blockInvalidChar: React.KeyboardEventHandler<HTMLInputElement> = (e) =>
    ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

  const props = { type: "number", onKeyDown: blockInvalidChar };
  const registerOpts = { valueAsNumber: true };

  return (
    <div className="flex items-end justify-between gap-4">
      <Input
        label={convert(field)}
        containerClass="basis-2/4 md:basis-3/4"
        required={!!field?.mandatory}
        error={!!info?.errors?.[field.uid]}
        placeholder={convertPlaceholder(field)}
        disabled={disabled ?? false}
        helperText={info?.errors?.[field.uid]?.message?.toString?.() || ""}
        {...props}
        {...register(field.uid, {
          required: field.mandatory ? "* Required" : false,
          ...registerOpts,
        })}
      />
      <Select
        containerClass="basis-2/4 md:basis-1/4"
        error={!!info?.errors?.[unitName]}
        disabled={disabled}
        required={!!field?.mandatory}
        defaultValue={""}
        helperText={info?.errors?.[unitName]?.message?.toString?.() || ""}
        {...register(unitName, {
          validate: (v) => {
            if (getValues(field.name) && !v) return "* Required";
            return true;
          },
        })}
      >
        <option hidden></option>
        {options?.map((e) => (
          <option key={`${field.id}-${e}`} value={e}>
            {e}
          </option>
        ))}
      </Select>
    </div>
  );
}

function RenderSelect({ field, disabled }: PropTypes) {
  const { register, getValues } = useFormContext();
  const convert = useGetFieldLabel();
  const convertPlaceholder = useGetFieldPlaceholder();
  const info = useFormState({ name: field.uid });

  let options: { id: number | boolean; name: string; active?: boolean }[] =
    field.type === "boolean"
      ? [
        { id: true, name: "Yes", active: true },
        { id: false, name: "No", active: true },
      ]
      : field?.options || [];
  let registerOpts: Parameters<typeof register>[1] = undefined;
  console.log("getValues", getValues())
  return (
    <Select
      label={convert(field)}
      error={!!info?.errors?.[field.uid]}
      // placeholder={convertPlaceholder(field)}
      disabled={field.uid === "name_of_service" || disabled}
      required={!!field?.mandatory}
      defaultValue={""}
      helperText={info?.errors?.[field.uid]?.message?.toString?.() || ""}
      {...register(field.uid, {
        ...(field.type === "boolean"
          ? {
            validate: (v) => {
              if (v === true || v === false || !field.mandatory) return true;
              return "* Required";
            },
            setValueAs: (v) => {
              if (v === "true") return true;
              if (v === "false") return false;
              return "";
            },
          }
          : {
            valueAsNumber: true,
            required: field.mandatory ? "* Required" : false,
          }),
      })}
    >
      <option hidden></option>
      {options
        ?.filter((e) => e.active)
        ?.map((e) => (
          <option key={`${field.id}-${e.id}`} value={String(e.id)}>
            {e.name}
          </option>
        ))}
    </Select>
  );
}

export function RenderField({ field, disabled }: PropTypes) {
  switch (field.type) {
    case "boolean":
    case "configurable-select":
    case "select":
      return <RenderSelect field={field} disabled={disabled} />;
    case "area":
      return <RenderArea field={field} disabled={disabled} />;
    case "integer":
    case "float":
    case "date":
    default:
      return (
        <RenderInput
          validator={field.validator}
          disabled={disabled}
          field={field}
        />
      );
  }
}

export function RenderDocumentConfigs({
  configs,
}: {
  configs: TDocumentConfig[];
}) {
  const { getValues } = useFormContext();
  const fieldValue = getValues("is_there_a_construction");
  console.log("get field", fieldValue)
  if (!configs.length) return null;

  return (
    <div className="mx-auto max-w-[1000px]">
      <table className="table-fixed w-full">
        <thead className="border-b border-dashed">
          <tr>
            <th className="w-[60%] p-3 text-xl border-r">Document Name</th>
            <th className="w-[40%] p-3 text-xl text-center">Upload</th>
          </tr>
        </thead>
        <tbody>
          {configs
            .filter((config) => {
              // Show the document Building Plan if fieldValue is true
              if (config.document_config_id === 1) {
                return fieldValue === true;
              }
              // Show the document Site Plan if fieldValue is false
              if (config.document_config_id === 2) {
                return fieldValue === false;
              }
              // By default, return all other documents
              return true;
            })
            // .map((e) => (
            //   <RenderDocumentConfig key={e.document_config_id} config={e} />
            // ))}
            .map((config) => (
              <RenderDocumentConfig
                key={config.document_config_id}
                config={{
                  ...config,
                  mandatory: config.document_config_id === 1 || config.document_config_id === 2 ? 1 : config.mandatory
                }}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
}

export function RenderDocumentConfig({ config }: { config: TDocumentConfig }) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const uploadMutation = useUploadFile();
  const [previewDocument, setPreviewDocument] = useState<{
    url: string;
  } | null>(null);
  const document = useController({
    name: `documents.${config.document_config_id}`,
    rules: { required: config.mandatory ? "* Required" : false },
  });

  const onUpload = async (file: File) => {
    try {
      if (config.file_type) {
        const types = config.file_type.split(",").filter((e) => !!e);
        if (!types.some((e) => file.type.includes(e))) {
          return ErrorToast(
            "Invalid file select, please select a different file!"
          );
        }
      }
      if (
        config.file_size &&
        ["KB", "MB"].includes(config?.file_size_unit || "")
      ) {
        let size = +config.file_size;
        const sizeInMB = file.size / (1024 * 1024);
        if (config.file_size_unit === "KB") {
          size /= 1024;
        }

        if (sizeInMB > size) {
          return ErrorToast(
            `Please select a small file, file cannot be more than ${config.file_size} ${config.file_size_unit}`
          );
        }
      }

      const result = await uploadMutation.mutateAsync(file);
      const url = result?.data?.data;
      if (!url) throw new Error();
      document.field.onChange(url);
    } catch (err) {
      ErrorToast("Something went wrong while uploading file!");
    }
  };

  return (
    <>
      <tr>
        <td className="p-3 border-r">
          <p className="grow text-grey-600">
            {config.document_label}
            {config.mandatory ? <span className="text-red-500"> *</span> : null}
          </p>
        </td>
        <td className="flex space-x-6 items-center justify-center p-3 border-0">
          {!document.field?.value ? (
            isMobile ? (
              <div>
                <label
                  htmlFor={document.field.name}
                  className={cx("bg-primary px-4 py-2 rounded block", {
                    ["border-red-500 border"]: document.fieldState?.error,
                  })}
                >
                  <i className="fa fa-upload m-0" />
                  <input
                    type="file"
                    id={document.field.name}
                    name={document.field.name}
                    onChange={(e) => {
                      const hit = e.target.files?.[0];
                      if (hit) onUpload(hit);
                    }}
                  />
                </label>
                {!!document?.fieldState?.error && (
                  <span className="text-xs text-red-500">
                    {document?.fieldState?.error?.message?.toString()}
                  </span>
                )}
              </div>
            ) : (
              <Input
                type="file"
                ref={document.field.ref}
                onBlur={document.field.onBlur}
                disabled={uploadMutation.isPending}
                error={!!document.fieldState?.error}
                onChange={(e) => {
                  const hit = e.target.files?.[0];
                  if (hit) onUpload(hit);
                }}
                helperText={document?.fieldState?.error?.message?.toString()}
                className="file:-my-3 file:-ml-4 file:h-[50px] file:border-0 file:mr-4 file:bg-primary file:px-4"
              />
            )
          ) : (
            <>
              <div className="flex justify-center space-x-1">
                <ViewButton
                  onClick={() => {
                    setPreviewDocument({ url: document.field.value });
                    // openInNewTab(document.field.value);
                  }}
                />
                <DeleteButton onClick={() => document.field.onChange(null)} />
                {/* <button onClick={() => openInNewTab(document.field.value)}>
              <i className="text-xl m-0 fa fa-eye"></i>
            </button>
            <button onClick={() => document.field.onChange(null)}>
              <i className="text-xl m-0 fa fa-trash-o"></i>
            </button> */}
              </div>
            </>
          )}
        </td>
      </tr>
      <DocumentPreviewDialog
        onClose={() => setPreviewDocument(null)}
        open={!!previewDocument}
      >
        {previewDocument &&
          previewDocument.url &&
          (previewDocument.url.endsWith("pdf") ? (
            <Worker
              workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
            >
              <Viewer fileUrl={previewDocument.url} />
            </Worker>
          ) : (
            <img
              src={previewDocument.url}
              className="object-contain w-full h-full"
              alt="Document"
            />
          ))}
      </DocumentPreviewDialog>
    </>
  );
}
